import { AlertTriangle, FileQuestion, Inbox, Lock, RefreshCw } from 'lucide-react';
import { ApiError, errorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** Estado vazio no estilo do votaDCR: cartão tracejado, ícone num círculo, título e a ação para começar. */
export function EmptyState({ title, description, action, className }: { title: string; description?: string; action?: React.ReactNode; className?: string }) {
  return (
    <div className={cn('bg-card flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-input px-6 py-14 text-center', className)}>
      <span className="bg-muted mb-2 flex size-16 items-center justify-center rounded-full">
        <Inbox className="text-muted-foreground size-7" aria-hidden />
      </span>
      <p className="text-lg font-medium">{title}</p>
      {description && <p className="text-muted-foreground mb-2 max-w-md text-sm">{description}</p>}
      {action}
    </div>
  );
}

/** Estado de erro: diferencia acesso negado (403), não encontrado (404) e demais. */
export function ErrorState({ error, onRetry, className }: { error: unknown; onRetry?: () => void; className?: string }) {
  const status = error instanceof ApiError ? error.status : 0;
  const Icon = status === 403 ? Lock : status === 404 ? FileQuestion : AlertTriangle;
  const title = status === 403 ? 'Acesso negado' : status === 404 ? 'Não encontrado' : 'Algo deu errado';
  const description =
    status === 403
      ? 'Você não tem permissão para ver ou alterar este conteúdo.'
      : status === 404
        ? 'O conteúdo não existe ou você não tem acesso a ele.'
        : errorMessage(error);
  return (
    <div role="alert" className={cn('flex flex-col items-center justify-center gap-2 p-8 text-center', className)}>
      <Icon className="text-muted-foreground size-8" aria-hidden />
      <p className="font-medium">{title}</p>
      <p className="text-muted-foreground max-w-md text-sm">{description}</p>
      {onRetry && status !== 403 && status !== 404 && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw /> Tentar novamente
        </Button>
      )}
    </div>
  );
}

export function ListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2" role="status" aria-label="Carregando">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

/** Cabeçalho da página num cartão branco (como no votaDCR): título em negrito à esquerda, ações à direita. */
export function PageHeader({ title, description, actions, leading }: { title: string; description?: React.ReactNode; actions?: React.ReactNode; leading?: React.ReactNode }) {
  return (
    <div className="bg-card flex flex-col gap-4 rounded-xl border p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:px-5">
      <div className="flex min-w-0 items-center gap-3">
        {leading}
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight break-words">{title}</h1>
          {description && <div className="text-muted-foreground mt-0.5 text-sm">{description}</div>}
        </div>
      </div>
      {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
    </div>
  );
}
